import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, LayersControl, LayerGroup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Machine icon - using the real Noodly machine photo
const machineIcon = L.icon({
  iconUrl: '/images/machine-icon.png',
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40],
  className: 'machine-marker'
});

// Licensee icon - Noodly N logo (VIP, only 2)
const licenseeIcon = L.icon({
  iconUrl: '/images/map-icon.png',
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40],
  className: 'licensee-marker'
});

// Manager icon - red marker (100 managers)
const managerIcon = L.icon({
  iconUrl: '/images/gestionnaire-icon.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
  className: 'manager-marker'
});

const NetworkMap = ({ globalView = false }) => {
  const [machines, setMachines] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    showMachines: true,
    showManagers: true,
    showLicensees: true
  });

  useEffect(() => {
    loadData();
  }, [globalView]);

  const loadData = async () => {
    try {
      // If globalView, load all data. Otherwise, load filtered data based on hierarchy
      const machinesEndpoint = globalView ? '/network/all-machines' : '/machines';
      const usersEndpoint = globalView ? '/network/all-contacts' : '/network/contacts';
      
      const [machinesRes, usersRes] = await Promise.all([
        axios.get(machinesEndpoint),
        axios.get(usersEndpoint)
      ]);
      
      setMachines(machinesRes.data.filter(m => m.latitude && m.longitude));
      setUsers(usersRes.data.filter(u => u.latitude && u.longitude && u.role !== 'groupe'));
    } catch (error) {
      console.error('Error loading network data:', error);
    } finally {
      setLoading(false);
    }
  };

  const licensees = users.filter(u => u.role === 'licencie');
  const managers = users.filter(u => u.role === 'gestionnaire');

  // Calculate center based on all entities
  const allEntities = [...machines, ...users];
  const centerLat = allEntities.length > 0
    ? allEntities.reduce((sum, e) => sum + e.latitude, 0) / allEntities.length
    : 46.603354;
  const centerLon = allEntities.length > 0
    ? allEntities.reduce((sum, e) => sum + e.longitude, 0) / allEntities.length
    : 1.888334;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-400">Chargement de la carte...</div>
      </div>
    );
  }

  return (
    <div className="relative h-full">
      {/* Filter Controls */}
      <div className="absolute top-4 right-4 z-[1000] bg-gray-900/95 p-3 rounded-lg border border-gray-700 shadow-lg">
        <div className="text-xs text-gray-400 mb-2 font-semibold">Affichage</div>
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.showMachines}
              onChange={(e) => setFilters({ ...filters, showMachines: e.target.checked })}
              className="w-4 h-4"
            />
            <img 
              src="/images/machine-icon.png" 
              alt="Machine" 
              className="w-5 h-5 object-contain"
            />
            <span className="text-sm text-white">Machines ({machines.length})</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.showManagers}
              onChange={(e) => setFilters({ ...filters, showManagers: e.target.checked })}
              className="w-4 h-4"
            />
            <img 
              src="/images/gestionnaire-icon.png" 
              alt="Gestionnaire" 
              className="w-5 h-5 object-contain"
            />
            <span className="text-sm text-white">Gestionnaires ({managers.length})</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.showLicensees}
              onChange={(e) => setFilters({ ...filters, showLicensees: e.target.checked })}
              className="w-4 h-4"
            />
            <img 
              src="/images/map-icon.png" 
              alt="Licencié" 
              className="w-5 h-5 object-contain"
            />
            <span className="text-sm text-white">Licenciés ({licensees.length})</span>
          </label>
        </div>
      </div>

      <MapContainer
        center={[centerLat, centerLon]}
        zoom={6}
        style={{ height: '100%', width: '100%' }}
      >
        {/* Dark theme tiles from CartoDB */}
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
        />
      
      <LayersControl position="topright">
        {/* Machines Layer */}
        {filters.showMachines && (
          <LayersControl.Overlay checked name={`Machines (${machines.length})`}>
            <MarkerClusterGroup
              chunkedLoading
              showCoverageOnHover={false}
              maxClusterRadius={50}
            >
            {machines.map((machine) => (
              <Marker
                key={machine.id}
                position={[machine.latitude, machine.longitude]}
                icon={machineIcon}
              >
                <Popup>
                  <div className="p-2 min-w-[200px]">
                    <div className="font-bold text-lg mb-2" style={{ color: '#d03367' }}>
                      🤖 Machine {machine.serial_number}
                    </div>
                    <div className="text-sm space-y-1">
                      <div><strong>Entreprise:</strong> {machine.company_name}</div>
                      <div><strong>Localisation:</strong> {machine.location}</div>
                      <div><strong>Ville:</strong> {machine.city}</div>
                      <div><strong>Stock:</strong> {machine.current_stock}/{machine.capacity}</div>
                      <div><strong>Statut:</strong> 
                        <span className={`ml-1 px-2 py-0.5 rounded text-xs ${
                          machine.status === 'active' ? 'bg-green-500/20 text-green-400' :
                          machine.status === 'maintenance' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {machine.status}
                        </span>
                      </div>
                      {machine.gestionnaire_code && (
                        <div className="mt-2 pt-2 border-t border-gray-300">
                          <div className="text-xs text-gray-600">
                            Gestionnaire: {machine.gestionnaire_code}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MarkerClusterGroup>
        </LayersControl.Overlay>
        )}

        {/* Licensees Layer */}
        {filters.showLicensees && (
          <LayersControl.Overlay checked name={`Licenciés (${licensees.length})`}>
          <MarkerClusterGroup
            chunkedLoading
            showCoverageOnHover={false}
            maxClusterRadius={50}
          >
            {licensees.map((licensee) => (
              <Marker
                key={licensee.id}
                position={[licensee.latitude, licensee.longitude]}
                icon={licenseeIcon}
              >
                <Popup>
                  <div className="p-2 min-w-[200px]">
                    <div className="font-bold text-lg mb-2" style={{ color: '#d03367' }}>
                      👤 {licensee.name}
                    </div>
                    <div className="text-sm space-y-1">
                      <div className="bg-red-500/20 text-red-400 px-2 py-1 rounded text-xs inline-block mb-2">
                        Licencié {licensee.code}
                      </div>
                      <div><strong>Email:</strong> {licensee.email}</div>
                      {licensee.phone && <div><strong>Téléphone:</strong> {licensee.phone}</div>}
                      <div><strong>Ville:</strong> {licensee.city}</div>
                      {licensee.address && <div><strong>Adresse:</strong> {licensee.address}</div>}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MarkerClusterGroup>
        </LayersControl.Overlay>
        )}

        {/* Managers Layer */}
        {filters.showManagers && (
          <LayersControl.Overlay checked name={`Gestionnaires (${managers.length})`}>
          <MarkerClusterGroup
            chunkedLoading
            showCoverageOnHover={false}
            maxClusterRadius={50}
          >
            {managers.map((manager) => (
              <Marker
                key={manager.id}
                position={[manager.latitude, manager.longitude]}
                icon={managerIcon}
              >
                <Popup>
                  <div className="p-2 min-w-[200px]">
                    <div className="font-bold text-lg mb-2" style={{ color: '#d03367' }}>
                      👨‍💼 {manager.name}
                    </div>
                    <div className="text-sm space-y-1">
                      <div className="bg-purple-500/20 text-purple-400 px-2 py-1 rounded text-xs inline-block mb-2">
                        Gestionnaire {manager.code}
                      </div>
                      <div><strong>Email:</strong> {manager.email}</div>
                      {manager.phone && <div><strong>Téléphone:</strong> {manager.phone}</div>}
                      <div><strong>Ville:</strong> {manager.city}</div>
                      {manager.address && <div><strong>Adresse:</strong> {manager.address}</div>}
                      {manager.parent_code && (
                        <div className="mt-2 pt-2 border-t border-gray-300">
                          <div className="text-xs text-gray-600">
                            Licencié: {manager.parent_code}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MarkerClusterGroup>
        </LayersControl.Overlay>
        )}
      </LayersControl>
    </MapContainer>
    </div>
  );
};

export default NetworkMap;
